import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Star, Award, Heart, Users, Quote, ExternalLink } from 'lucide-react';
import { vendorSuccessStoriesService } from '../../services/vendorSuccessStoriesService';
import { useServiceCategories } from '../../services/serviceCategoryService';
import StoryDetailModal, { QUOTE_MAX_LENGTH } from '../../components/client/StoryDetailModal';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingPrimaryButton,
  MarketingGhostOnDark,
  MarketingHeroLightButton,
} from '../../components/home/MarketingPageChrome';

const STORIES_HERO =
  'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=1800&q=80';

const DEFAULT_ICONS = { Award, Heart, Users };

const VendorSuccessStories = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedStory, setSelectedStory] = useState(null);

  const { data: categoriesResponse } = useServiceCategories({ per_page: 50 });

  const { data: response, isLoading } = useQuery({
    queryKey: ['vendorSuccessStories'],
    queryFn: () => vendorSuccessStoriesService.get(),
    staleTime: 5 * 60 * 1000,
  });

  const tabs = useMemo(() => {
    const categories = categoriesResponse?.data?.data || categoriesResponse?.data || [];
    const base = [{ id: 'all', label: 'All stories', icon: Trophy }];
    const categoryTabs = Array.isArray(categories)
      ? categories
          .filter((c) => c && c.is_active !== false)
          .map((c, i) => ({
            id: (c.category_name || c.id || '').toLowerCase(),
            label: c.category_name || c.id,
            icon: DEFAULT_ICONS[Object.keys(DEFAULT_ICONS)[i % 3]] || Award,
          }))
      : [];
    return [...base, ...categoryTabs];
  }, [categoriesResponse]);

  const currentStories = useMemo(() => {
    const allStories = response?.data || [];
    if (activeTab === 'all') return allStories;
    return allStories.filter((s) => s.category?.toLowerCase() === activeTab);
  }, [response?.data, activeTab]);

  const truncateQuote = (quote) => {
    if (!quote) return '';
    if (quote.length <= QUOTE_MAX_LENGTH) return quote;
    return `${quote.slice(0, QUOTE_MAX_LENGTH).trim()}…`;
  };

  return (
    <MarketingPage>
      <MarketingMediaHero
        title="Success stories"
        subtitle="How vendors are growing their businesses with VendaList."
        image={STORIES_HERO}
        actions={
          <>
            <MarketingHeroLightButton to="/register">Become a vendor</MarketingHeroLightButton>
            <MarketingGhostOnDark to="/how-it-works">How it works</MarketingGhostOnDark>
          </>
        }
      />

      <MarketingSection tone="white">
        <MarketingSplitIntro title="Real vendors. Real growth.">
          <p>
            From first bookings to steady calendars — these are the partners building careers on
            VendaList. Browse by category or open a story for the full quote.
          </p>
        </MarketingSplitIntro>
      </MarketingSection>

      <MarketingSection tone="white" className="!pt-0 sm:!pt-0">
        <div className="mx-auto max-w-6xl">
          <nav className="mb-10 flex gap-1 overflow-x-auto border-b border-m-border-light scrollbar-hide">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                    active
                      ? 'border-m-ink text-m-ink'
                      : 'border-transparent text-m-ink-muted hover:text-m-ink'
                  }`}
                >
                  <IconComponent className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          {isLoading ? (
            <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="mb-4 aspect-[4/3] bg-m-canvas-light" />
                  <div className="mb-2 h-4 w-2/3 rounded bg-m-canvas-light" />
                  <div className="h-3 w-full rounded bg-m-canvas-light" />
                </div>
              ))}
            </div>
          ) : currentStories.length > 0 ? (
            <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3">
              {currentStories.map((story) => (
                <button
                  key={story.id}
                  type="button"
                  onClick={() => setSelectedStory(story)}
                  className="group block text-left"
                  style={{ color: '#0a0a0a' }}
                >
                  <div className="relative mb-4 aspect-[4/3] overflow-hidden bg-m-canvas-light">
                    <img
                      src={
                        story.image ||
                        'https://via.placeholder.com/800x600?text=Vendor+Story'
                      }
                      alt={story.vendor_name}
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                      onError={(e) => {
                        e.target.src =
                          'https://via.placeholder.com/800x600?text=Vendor+Story';
                      }}
                    />
                    {story.featured ? (
                      <div className="absolute left-3 top-3 bg-m-ink/85 px-2 py-0.5 text-[10px] font-medium text-white">
                        Featured
                      </div>
                    ) : null}
                  </div>

                  <div className="mb-2 flex items-center gap-1.5">
                    <Star className="h-3.5 w-3.5 fill-current" />
                    <span className="text-sm font-bold">{story.vendor_name}</span>
                    {story.category ? (
                      <span className="text-xs capitalize" style={{ color: '#8a8a8a' }}>
                        · {story.category}
                      </span>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Quote className="mb-1 h-4 w-4 text-m-ink-faint" />
                    <p className="line-clamp-3 text-sm leading-relaxed" style={{ color: '#6b6b6b' }}>
                      “{truncateQuote(story.quote)}”
                    </p>
                    {story.quote?.length > QUOTE_MAX_LENGTH ? (
                      <span className="mt-1 inline-block text-xs font-medium">Read more →</span>
                    ) : null}
                  </div>

                  <p className="text-xs font-medium">{story.author}</p>
                  <p className="mb-3 text-xs" style={{ color: '#6b6b6b' }}>
                    {story.role}
                  </p>

                  {story.vendor_id ? (
                    <Link
                      to={`/vendor/${story.vendor_id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1.5 text-xs font-medium underline-offset-2 hover:underline"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      View vendor
                    </Link>
                  ) : null}
                </button>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Trophy className="mx-auto mb-4 h-10 w-10 text-m-ink-faint" />
              <p className="text-sm" style={{ color: '#6b6b6b' }}>
                No success stories in this category yet.
              </p>
            </div>
          )}
        </div>
      </MarketingSection>

      <MarketingSection tone="charcoal">
        <MarketingReveal className="mx-auto max-w-2xl text-center">
          <h2 className="marketing-section-title mb-3" style={{ color: '#ffffff' }}>
            Ready to write your story?
          </h2>
          <p className="mb-8 text-[1.05rem] leading-relaxed text-white/70">
            Join vendors growing their businesses on VendaList.
          </p>
          <div className="flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            <MarketingPrimaryButton
              to="/register"
              className="!bg-white !text-m-ink hover:!bg-white/90"
            >
              Become a vendor
            </MarketingPrimaryButton>
            <MarketingGhostOnDark to="/how-it-works">Learn more</MarketingGhostOnDark>
          </div>
        </MarketingReveal>
      </MarketingSection>

      {selectedStory ? (
        <StoryDetailModal story={selectedStory} onClose={() => setSelectedStory(null)} />
      ) : null}
    </MarketingPage>
  );
};

export default VendorSuccessStories;
